import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./state/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RoleGate } from "./components/RoleGate";
import { KIOSK_PATH, armedTabletMayEnter, kioskAllowedOnThisDevice } from "./components/kioskAccess";
import { KioskShellOnly } from "./components/KioskShellOnly";
import { isNativeShell } from "./platform/shell";
import { getShopFloorSessionId } from "./api/apiClient";
import { ShopModeProvider, useShopMode } from "./state/ShopModeContext";
import { NotificationsProvider } from "./state/NotificationsContext";
import { InTheShopPage } from "./routes/InTheShopPage";
import { LoginPage } from "./routes/LoginPage";
import { ForgotPasswordPage } from "./routes/account/ForgotPasswordPage";
import { AccountSetupPage } from "./routes/account/AccountSetupPage";
import { AccountResetPage } from "./routes/account/AccountResetPage";
import { AccountConfirmEmailPage } from "./routes/account/AccountConfirmEmailPage";
import { DashboardLayout } from "./routes/DashboardLayout";
import { InventoryPage } from "./routes/InventoryPage";
import { ItemCreatePage } from "./routes/ItemCreatePage";
import { CatalogDraftsPage } from "./routes/CatalogDraftsPage";
import { ItemEditPage } from "./routes/ItemEditPage";
import { ItemsManagePage } from "./routes/ItemsManagePage";
import { ProfilePage } from "./routes/ProfilePage";
import { UserCreatePage } from "./routes/UserCreatePage";
import { UsersPage } from "./routes/UsersPage";
import { ReceiptPrinterPage } from "./routes/ReceiptPrinterPage";
import { SalesPage } from "./routes/SalesPage";
import { SaleNewPage } from "./routes/SaleNewPage";
import { LocationManagePage } from "./routes/LocationManagePage";
import { LocationRedirect } from "./routes/LocationRedirect";
import { LocationSchedulePage } from "./routes/LocationSchedulePage";
import { SchedulePage } from "./routes/SchedulePage";
import { OrdersCalendarPage } from "./routes/OrdersCalendarPage";
import { OrderBuildPage } from "./routes/OrderBuildPage";
import { DispatchPage } from "./routes/DispatchPage";
import { PayoutRulesPage } from "./routes/PayoutRulesPage";
import { CourierJobsPage } from "./routes/CourierJobsPage";
import { MyShiftsPage } from "./routes/MyShiftsPage";
import { SupplierNewPage } from "./routes/SupplierNewPage";
import { SuppliersPage } from "./routes/SuppliersPage";
import { BouquetTemplatesPage } from "./routes/BouquetTemplatesPage";
import { BouquetTemplateNewPage } from "./routes/BouquetTemplateNewPage";
import { BouquetBuildPage } from "./routes/BouquetBuildPage";
import { YieldInsightsPage } from "./routes/YieldInsightsPage";
import { FinancesPage } from "./routes/FinancesPage";
import { ActionLogPage } from "./routes/ActionLogPage";
import { AnalyticsPage } from "./routes/AnalyticsPage";
import { KitPreviewPage } from "./routes/KitPreviewPage";
import { CouriersPage } from "./routes/CouriersPage";
import { CourierCalendarPage } from "./routes/CourierCalendarPage";
import { CourierPlanningPage } from "./routes/CourierPlanningPage";
import { DeliveriesPage } from "./routes/DeliveriesPage";
import { DeliveryNewPage } from "./routes/DeliveryNewPage";
import { DeliveryDetailPage } from "./routes/DeliveryDetailPage";
import { InvoiceDetailPage } from "./routes/InvoiceDetailPage";
import { InvoiceUploadPage } from "./routes/InvoiceUploadPage";
import { SupplierPapersPage } from "./routes/SupplierPapersPage";
import { MainPage } from "./routes/MainPage";
import { BlogPostsPage } from "./routes/BlogPostsPage";
import { BlogPostDetailPage } from "./routes/BlogPostDetailPage";
import { FeedbackTicketsPage } from "./routes/FeedbackTicketsPage";
import { BugReportsPage } from "./routes/BugReportsPage";
import { TelegramPage } from "./routes/TelegramPage";
import { AiControlPage } from "./routes/AiControlPage";
import { ShopifyPage } from "./routes/ShopifyPage";
import { ShopifyOrdersPage } from "./routes/ShopifyOrdersPage";
import { ShopifyOrdersPersonPage } from "./routes/ShopifyOrdersPersonPage";
import { ShopifyProductsPage } from "./routes/ShopifyProductsPage";

// G-15: the flat "New order" page retired into the unified Build & send flow;
// old links/bookmarks redirect with their query params intact.
function OrderNewRedirect() {
  const [searchParams] = useSearchParams();
  const query = searchParams.toString();
  return <Navigate to={`/app/orders/build${query ? `?${query}` : ""}`} replace />;
}

/**
 * The kiosk route's gate.
 *
 * A ROLE is required to ARM a tablet, and that check is untouched. But an
 * ALREADY-armed tablet holds its own credential, so when its CRM session ends
 * it must keep its lock screen rather than being bounced to /login — otherwise
 * a shop tablet left untouched for thirty minutes stops being a kiosk, taking
 * the G-79 order alert with it (owner report, 2026-09-14).
 *
 * The condition lives in `kioskAccess` because `ProtectedRoute` has to agree
 * with it; two copies would drift.
 */
function KioskRoute() {
  const { isAuthenticated } = useAuth();
  // G-133 / OD-168: the tablet app only — every browser gets the explanation.
  if (!kioskAllowedOnThisDevice(isNativeShell(), import.meta.env.DEV)) {
    return <KioskShellOnly />;
  }
  if (armedTabletMayEnter(isAuthenticated, getShopFloorSessionId())) {
    return <InTheShopPage />;
  }
  return (
    <RoleGate allow={["OWNER", "ADMIN", "MANAGER", "FLORIST"]}>
      <InTheShopPage />
    </RoleGate>
  );
}

/**
 * Thin layout that provides ShopModeContext to every protected route.
 * Placed between ProtectedRoute and DashboardLayout so context is shared
 * across both the normal dashboard and the kiosk /app/in-the-shop route.
 */
/**
 * AN ARMED TABLET DOES NOT LEAVE THE KIOSK.
 *
 * Owner scenario, 2026-09-14: a manager arms the tablet and walks out; a
 * florist presses back (or taps "Back to the app" after disarming) and lands
 * inside the manager's CRM — orders, finances, staff — because arming never
 * ended the manager's session and nothing kept the tablet on its own screen.
 *
 * Verified before building: NOTHING in the router keyed on `isArmed`, so the
 * Android back button alone was enough — `/app` is in history because the
 * manager navigated FROM there to arm.
 *
 * This is the containment half. The CRM session may still exist in memory, but
 * while the tablet is armed there is no route out of the kiosk to spend it on.
 * Taking the tablet OUT of kiosk mode is the other half, and it now demands a
 * fresh sign-in.
 */
function KioskLock() {
  const { isArmed, sessionChecked } = useShopMode();
  const location = useLocation();

  /*
   * On a FRESH page load `isArmed` is false until `GET /shop-mode/status`
   * answers, so without this a reload on an armed tablet would paint the CRM
   * for a round trip before the redirect caught up — the very screen this
   * exists to hide. Held only when the tablet actually carries a session id,
   * which is a synchronous read, so a phone or a desk browser never waits.
   */
  if (!sessionChecked && getShopFloorSessionId()) return null;

  if (isArmed && location.pathname !== KIOSK_PATH) {
    return <Navigate to={KIOSK_PATH} replace />;
  }
  return <Outlet />;
}

function ShopModeLayout() {
  return (
    <ShopModeProvider>
      <NotificationsProvider>
        <KioskLock />
      </NotificationsProvider>
    </ShopModeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<LoginPage />} />
        {/* G-136 — emailed account links and "Forgot password?": public, no session. */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/account/setup" element={<AccountSetupPage />} />
        <Route path="/account/reset" element={<AccountResetPage />} />
        <Route path="/account/confirm-email" element={<AccountConfirmEmailPage />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<ShopModeLayout />}>
          <Route path="/app" element={<DashboardLayout />}>
            <Route index element={<MainPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="balance" element={<Navigate to="/app/inventory" replace />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route
              path="suppliers"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <SuppliersPage />
                </RoleGate>
              }
            />
            <Route
              path="suppliers/new"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <SupplierNewPage />
                </RoleGate>
              }
            />
            <Route
              path="bouquets/templates"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <BouquetTemplatesPage />
                </RoleGate>
              }
            />
            <Route
              path="bouquets/templates/new"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <BouquetTemplateNewPage />
                </RoleGate>
              }
            />
            <Route
              path="bouquets/build"
              element={
                <RoleGate allow={["OWNER", "MANAGER", "FLORIST"]}>
                  <BouquetBuildPage />
                </RoleGate>
              }
            />
            <Route
              path="insights/yield"
              element={
                <RoleGate allow={["OWNER"]}>
                  <YieldInsightsPage />
                </RoleGate>
              }
            />
            <Route
              path="items"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <ItemsManagePage />
                </RoleGate>
              }
            />
            <Route
              path="items/new"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <ItemCreatePage />
                </RoleGate>
              }
            />
            <Route
              path="items/drafts"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <CatalogDraftsPage />
                </RoleGate>
              }
            />
            <Route
              path="items/:id"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <ItemEditPage />
                </RoleGate>
              }
            />
            {/* G-94 S4: the shop dashboard retired into the inventory page.
                The redirect carries the query string, because G-83's
                create-then-receive and G-92's action sheet both arrive here
                with `?receiveForStock=`. */}
            <Route path="locations/:locationId" element={<LocationRedirect />} />
            <Route
              path="schedule"
              element={
                <RoleGate allow={["OWNER", "MANAGER", "FLORIST"]}>
                  <SchedulePage />
                </RoleGate>
              }
            />
            <Route path="tasks" element={<Navigate to="/app/in-the-shop" replace />} />
            <Route
              path="orders"
              element={
                <RoleGate allow={["OWNER", "MANAGER", "FLORIST"]}>
                  <OrdersCalendarPage />
                </RoleGate>
              }
            />
            <Route path="orders/new" element={<OrderNewRedirect />} />
            <Route
              path="orders/build"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <OrderBuildPage />
                </RoleGate>
              }
            />
            <Route
              path="orders/build/:orderId"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <OrderBuildPage />
                </RoleGate>
              }
            />
            <Route
              path="dispatch"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <DispatchPage />
                </RoleGate>
              }
            />
            <Route
              path="payout-rules"
              element={
                <RoleGate allow={["OWNER"]}>
                  <PayoutRulesPage />
                </RoleGate>
              }
            />
            <Route
              path="courier/deliveries"
              element={
                <RoleGate allow={["COURIER"]}>
                  <CourierJobsPage />
                </RoleGate>
              }
            />
            <Route
              path="courier/calendar"
              element={
                <RoleGate allow={["COURIER"]}>
                  <CourierCalendarPage />
                </RoleGate>
              }
            />
            <Route
              path="locations/:locationId/schedule"
              element={
                <RoleGate allow={["OWNER", "MANAGER", "FLORIST"]}>
                  <LocationSchedulePage />
                </RoleGate>
              }
            />
            <Route
              path="locations/manage"
              element={
                <RoleGate allow={["OWNER"]}>
                  <LocationManagePage />
                </RoleGate>
              }
            />
            <Route path="locations/new" element={<Navigate to="/app/locations/manage" replace />} />
            <Route path="my/shifts" element={<MyShiftsPage />} />
            <Route
              path="users"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <UsersPage />
                </RoleGate>
              }
            />
            {/* G-64: designing the ticket and pairing the printer are both
                once-per-shop setup jobs, so they sit on the staff plane rather
                than behind arming kiosk mode and a PIN. OD-35 lets owner and
                admin leave the kiosk inside the tablet app. The path stays
                `printer` so a paired tablet's bookmark still works. */}
            <Route
              path="printer"
              element={
                <RoleGate allow={["OWNER", "ADMIN"]}>
                  <ReceiptPrinterPage />
                </RoleGate>
              }
            />
            <Route
              path="users/new"
              element={
                <RoleGate allow={["OWNER"]}>
                  <UserCreatePage />
                </RoleGate>
              }
            />
            <Route
              path="sales"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <SalesPage />
                </RoleGate>
              }
            />
            <Route
              path="sales/new"
              element={
                <RoleGate allow={["OWNER", "MANAGER", "FLORIST"]}>
                  <SaleNewPage />
                </RoleGate>
              }
            />
            <Route
              path="finances"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <FinancesPage />
                </RoleGate>
              }
            />
            <Route
              path="analytics"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <AnalyticsPage />
                </RoleGate>
              }
            />
            <Route
              path="action-log"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <ActionLogPage />
                </RoleGate>
              }
            />
            <Route
              path="kit"
              element={
                <RoleGate allow={["OWNER"]}>
                  <KitPreviewPage />
                </RoleGate>
              }
            />
            <Route
              path="couriers"
              element={
                <RoleGate allow={["OWNER"]}>
                  <CouriersPage />
                </RoleGate>
              }
            />
            <Route
              path="couriers/:courierId/schedule"
              element={
                <RoleGate allow={["OWNER"]}>
                  <CourierPlanningPage />
                </RoleGate>
              }
            />
            <Route
              path="deliveries"
              element={
                <RoleGate allow={["OWNER", "MANAGER", "FLORIST"]}>
                  <DeliveriesPage />
                </RoleGate>
              }
            />
            <Route
              path="deliveries/new"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <DeliveryNewPage />
                </RoleGate>
              }
            />
            <Route
              path="deliveries/:id"
              element={
                <RoleGate allow={["OWNER", "MANAGER", "FLORIST"]}>
                  <DeliveryDetailPage />
                </RoleGate>
              }
            />
            {/* G-131 (owner, 2026-10-09): every uploaded paper, and what waits for a factura. */}
            <Route
              path="invoices"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <SupplierPapersPage />
                </RoleGate>
              }
            />
            <Route
              path="invoices/upload"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <InvoiceUploadPage />
                </RoleGate>
              }
            />
            <Route
              path="invoices/:id"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <InvoiceDetailPage />
                </RoleGate>
              }
            />
            <Route
              path="blog-posts"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <BlogPostsPage />
                </RoleGate>
              }
            />
            <Route
              path="blog-posts/:id"
              element={
                <RoleGate allow={["OWNER", "MANAGER"]}>
                  <BlogPostDetailPage />
                </RoleGate>
              }
            />
            <Route
              path="shopify"
              element={
                <RoleGate allow={["OWNER", "ADMIN"]}>
                  <ShopifyPage />
                </RoleGate>
              }
            />
            {/* G-149 (OD-230): owners and admins only — managers never see it. */}
            <Route
              path="ai-control"
              element={
                <RoleGate allow={["OWNER", "ADMIN"]}>
                  <AiControlPage />
                </RoleGate>
              }
            />
            {/* G-122. Owner/admin like the Shopify connection: it holds
                people's Telegram IDs. */}
            <Route
              path="telegram"
              element={
                <RoleGate allow={["OWNER", "ADMIN"]}>
                  <TelegramPage />
                </RoleGate>
              }
            />
            <Route
              path="shopify/products"
              element={
                <RoleGate allow={["OWNER", "ADMIN", "MANAGER"]}>
                  <ShopifyProductsPage />
                </RoleGate>
              }
            />
            <Route
              path="shopify-orders"
              element={
                <RoleGate allow={["OWNER", "ADMIN", "MANAGER"]}>
                  <ShopifyOrdersPage />
                </RoleGate>
              }
            />
            <Route
              path="shopify-orders/people/:userId"
              element={
                <RoleGate allow={["OWNER", "ADMIN", "MANAGER"]}>
                  <ShopifyOrdersPersonPage />
                </RoleGate>
              }
            />
            <Route
              path="feedback"
              element={
                <RoleGate allow={["OWNER"]}>
                  <FeedbackTicketsPage />
                </RoleGate>
              }
            />
            {/* G-72. OWNER only, like the backlog beside it — a report carries a
                screenshot of whatever was on the counter's screen, which is
                routinely a customer's name and delivery address. */}
            <Route
              path="bug-reports"
              element={
                <RoleGate allow={["OWNER"]}>
                  <BugReportsPage />
                </RoleGate>
              }
            />
          </Route>

          {/* Kiosk route — outside DashboardLayout, has its own full-page layout */}
          <Route
            path="/app/in-the-shop"
            element={
              // OD-23 (2026-08-04): florists open shops alone and must be able
              // to arm the kiosk; OD-20's ceiling caps what a florist-armed
              // tablet admits, so this widens access, never privilege.
              <KioskRoute />
            }
          />
          </Route>{/* end ShopModeLayout */}
        </Route>
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </AuthProvider>
  );
}
